import type { ComponentVersionDependencyMetadataApi } from '@ingenico/api-sdk-device-management-private';
import { enumVersionPublicationStatus } from '@ingenico/api-sdk-device-management-private';
import type {
  ApplicationResourceReadV2Api,
  ApplicationVersionResourceListResponseV2Api,
  BffApplicationVersionResourceReadV2Api,
  SearchFieldListResponseApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import type { FieldConfiguration } from '@ingenico/react-ui/advanced-search';
import { convertByteToFileSizeText } from '@ingenico/util-common';
import { isEqual, toArray, toString } from 'lodash-es';
import type { OverViewPayloadDTO, VersionDTO } from '../../../components/overview-tab/Overviewtab.model';
import { VersionStatus } from '../../../pages/application-details-page/ApplicationDetailsPage.model';
import type {
  MappedApplicationVersionResourceListResponseV2Api,
  MappedApplicationVersionResourceReadV2Api,
} from '../../models/applicationVersion.model';
import { toIsoStringIfDate } from '../utils/date.utils';

export function mapApplicationById({
  name,
  status,
  longDescription,
  shortDescription,
  compatibleDeviceFamilyNames,
  creationDate,
  provider,
  id,
  publisherId,
  type,
  assets,
  publishedVersionNumbers,
}: ApplicationResourceReadV2Api): Omit<
  OverViewPayloadDTO,
  'latestVersion' | 'latestVersionId' | 'markdown' | 'OSVersion' | 'publicationDate' | 'subtitle'
> {
  const computedStatus = isEqual(enumVersionPublicationStatus.PUBLISHED, status)
    ? enumVersionPublicationStatus.PUBLISHED.toLowerCase()
    : enumVersionPublicationStatus.DEPRECATED.toLowerCase();
  const computedCreationDate = toIsoStringIfDate(creationDate);
  return {
    provider,
    title: toString(name),
    status: { label: computedStatus, type: computedStatus },
    longDescription: toString(longDescription),
    shortDescription: toString(shortDescription),
    deviceFamilies: toArray(compatibleDeviceFamilyNames),
    creationDate: computedCreationDate,
    id,
    publisherId,
    type: toString(type),
    assets,
    publishedVersions: toArray(publishedVersionNumbers),
  };
}

export function mapOsVersionByApplicationIdOverview({
  data,
}: ApplicationVersionResourceListResponseV2Api): Pick<
  OverViewPayloadDTO,
  'latestVersion' | 'latestVersionId' | 'markdown' | 'OSVersion' | 'publicationDate'
>[] {
  return data.map(({ longDescription, creationDate, versionNumber, id }) => {
    const computedCreationDate = toIsoStringIfDate(creationDate);

    return {
      latestVersionId: toString(id),
      markdown: toString(longDescription),
      publicationDate: computedCreationDate,
      latestVersion: toString(versionNumber),
      OSVersion: {
        name: 'OS_VERSION name',
        version: ['OS_VERSION version'],
      },
    };
  });
}

export function mapOsVersionByApplicationIdVersion(
  response: ApplicationVersionResourceListResponseV2Api,
): MappedApplicationVersionResourceListResponseV2Api {
  return {
    ...response,
    data: response?.data?.map((item) => {
      const computedCreationDate = toIsoStringIfDate(item?.creationDate);
      const computedStatus = isEqual(enumVersionPublicationStatus.PUBLISHED, item?.status)
        ? VersionStatus.PUBLISHED
        : VersionStatus.DEPRECATED;
      return {
        version: toString(item?.versionNumber),
        id: toString(item?.id),
        creationDate: computedCreationDate,
        size: convertByteToFileSizeText(item?.size ?? 0),
        status: computedStatus,
        ...item,
      } as MappedApplicationVersionResourceReadV2Api;
    }),
  };
}

export function mapComponentVersionDependencyMetadataApiToVersionDTO(
  data: ComponentVersionDependencyMetadataApi[],
): VersionDTO {
  const firstName = data?.[0]?.name;
  return {
    name: toString(firstName),
    version: toArray(data.map(({ versionNumber }) => toString(versionNumber))),
  };
}
export function mapBffOsVersionByApplicationId({
  applicationDependencyVersions,
  compatibleOsVersions,
  systemPackageDependencyVersions,
}: BffApplicationVersionResourceReadV2Api): Pick<
  OverViewPayloadDTO,
  'applicationVersion' | 'OSVersion' | 'systemPackageVersion'
> {
  return {
    OSVersion: mapComponentVersionDependencyMetadataApiToVersionDTO(compatibleOsVersions),
    applicationVersion: mapComponentVersionDependencyMetadataApiToVersionDTO(applicationDependencyVersions),
    systemPackageVersion: mapComponentVersionDependencyMetadataApiToVersionDTO(systemPackageDependencyVersions),
  };
}

export function mapAppVersionsMinimalListToFieldConfiguration(
  response: SearchFieldListResponseApi,
): FieldConfiguration[] {
  return response.map(({ key, type }) => ({
    key,
    type,
    visible: true,
    searchable: true,
  })) as FieldConfiguration[];
}
